/*******************************************************************************
 * Copyright     : Copyright (c)  2024 Honeywell Inc. All rights reserved.
 *                 This work contains trade secrets and confidential materials
 *                 of Honeywell Inc.No part may be reproduced or transmitted in any form by any
 *                 means or for anypurpose without the express written permission of Honeywell. *
 *******************************************************************************/
import React, { useCallback, useEffect, useState, useMemo } from "react";

//@Packages
import { useLocation } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { useDispatch, useSelector } from "react-redux";
import {
  Select,
  Button,
  Card,
  CardContent,
  Control,
  Input,
  Label,
  PageLoader,
  SelectOption,
  ControlError,
  SelectOptionSearch,
  Badge,
  SuccessIcon,
  InfoIcon,
  ProgressIcon,
  AlertIcon,
  ContentLoader,
  Modal,
  ControlHelper,
  EmptyState
} from "@forge/common";

//@Models
import { ISiteListProps, OrgListProps, SiteMetaDataProps } from "../../models";
import {
  IBuildOrganisation,
  DeviceListProps,
  GatewayEditBasicInfoProps,
  GatewaySiteProps,
  MetaDataStatusObjProps,
  MetaDataStatusProps,
  ToastStateInfoProps
} from "../../models/devicelist";

//@Store
import { RootState } from "../../store";

//@Components
import Toast from "../../reusablecomponents/toast";
import OverviewTable from "../viewdevice/overview/dataCardTable/OverviewTable";

//@Services
import {
  useGetDeviceDetailsQuery,
  useUpdateGatewayMutation,
  useGetHardwareDetailsQuery
} from "../../services/api";
import {
  useGetOrgListsQuery,
  useGetOrgMetadataQuery,
  useGetOrgSiteListsQuery
} from "../../services/rbm-api";
import { useSyncOrganizationMutation } from "../../services/file-service-api/fileServeApi";

//Configuration Variables
import { ConfigurationVariables } from "../../config";

//@Constants
import {
  orgStatusLists,
  BASIC_INFO_MAX_CHARS,
  BASIC_INFO_MIN_CHARS,
  orgSiteStatusNameLists,
  emailText,
  GatewayStatus,
  LOCALE_CONST
} from "../../utils/constants";

//@Actions
import { setDeviceListUpdated, setHeaderDetails, setRBMDetails } from "../../actions";

//@Utils
import { useNavigateToLandingPage } from "../../utils/navigationUtils";
import { areListsEqual, displayStatus, gatewayErrorStatusMessages, getStatusColor, validateInput, validateSpecialChar, validateAlphanumeric, validateHardwareModel, validateDeviceName } from "../../utils/utility";

//@Styles
import "./index.scss";
import HardwareDetails from "../EBICognition/HardwareDetails";

//@Types
interface HardwareErrorState {
  showDeviceTypeError: boolean;
  showHardwareModelError: boolean;
  showHardwareIDError: boolean;
  showValidHardwareIDError: boolean;
  showHardwareModelTypeError: boolean;
}

//@Functional Component
const EditDevice = () => {
  const dispatch = useDispatch();
  const { t } = useTranslation();
  const { pathname: locationId } = useLocation();

  const orgLists = useSelector((state: RootState) => state.global.orgLists);
   const selectedOrg = useSelector((state: RootState) => state.global.selectedOrg);
  const rbmAccessToken = useSelector((state: RootState) => state.global.rbmAccessToken);
  const [isConfirmationModalOpen, setIsConfirmationModalOpen] = useState(false);

  const [orgSearchQuery, setOrgSearchQuery] = useState<string>("");

  const [deviceId, setDeviceId] = useState("");
  const [deviceName, setDeviceName] = useState("");
  const [selectedOrgValId, setSelectedOrgValId] = useState("");
  const [hardwareData, setHardwareData] = useState<any>({
    deviceType: "",
    hardwareModel: "",
    hardwareID: ""
  });
  const [hardwareErrorState, setHardwareErrorState] = useState<HardwareErrorState>({
    showDeviceTypeError: false,
    showHardwareModelError: false,
    showHardwareIDError: false,
    showValidHardwareIDError: false,
    showHardwareModelTypeError: false,
  });

  const [orgSiteStatusVals, setOrgSiteStatusVals] = useState<{
    [key: string]: string;
  }>({
    CbSiteId: "",
    EomSiteId: ""
  });
  const [orgStatusVals, setOrgStatusVals] = useState<{ [key: string]: string }>(
    {
      failedStatus: "",
      completedStatus: "",
      inProgressStatus: "",
      onboardingStatus: ""
    }
  );

  const [siteLists, setSiteLists] = useState<ISiteListProps[]>([]);
  const [selectedOrgSiteId, setSelectedOrgSiteId] = useState<
    GatewaySiteProps[]
  >([]);

  const [retryType, setRetryType] = useState("");
  const [apiLoading, setApiLoading] = useState<boolean>(false);
  const [isSiteLoading, setIsSiteLoading] = useState<boolean>(false);
  const [toastInfo, setToastInfo] = useState<ToastStateInfoProps>({
    show: false,
    type: "success",
    content: ""
  });
  const [orgMetaStatus, setOrgMetaStatus] = useState<MetaDataStatusProps>({
    status: "",
    message: ""
  });
  const [siteMetaStatus, setSiteMetaStatus] = useState<MetaDataStatusObjProps>(
    {}
  );
  const [isFormChanged, setIsFormChanged] = useState<boolean>(false);
  const [errorState, setErrorState] = useState({
    showNameError: false,
    showNameTypeError: false,
  });
  const {
    data: deviceRec,
    isLoading,
    error: deviceDataError,
    isError,
    refetch: refetchDeviceDetails
  } = useGetDeviceDetailsQuery({ deviceId }, { skip: !deviceId });

  const {
    data: hardwareDetailsData,
    isLoading: isHardwareLoading,
    error: hardwareError,
    isError: isHardwareError
  } = useGetHardwareDetailsQuery(undefined);

  const {
    data: orgMetaData,
    refetch: orgRefresh,
    isLoading: orgMetaLoading,
    isFetching: orgMetaFetching
  } = useGetOrgMetadataQuery(
    { orgId: selectedOrgValId },
    { skip: !selectedOrgValId }
  );

  const getOnBoardStatus = () =>
    orgMetaData?.find(
      (item: { key: string }) => item.key === orgStatusVals?.onboardingStatus
    )?.value || "";

  const lcbsQueryParam = /^(dvm|ebi[\s_-]?platform)$/i.test(String(deviceRec?.gatewayType || ""))
    ? "?fetchOnPrem=true"
    : "";

  const { data: orgDataLists } = useGetOrgListsQuery(lcbsQueryParam, {
    skip: !deviceRec,
    refetchOnMountOrArgChange: true
  });

  const getIsOnPremValueFromOrgRecord = (orgRecord: unknown) => {
    const record = orgRecord as { isOnPrem?: unknown };
    return String(record?.isOnPrem ?? "").trim();
  };

  const selectedOrgRecord = useMemo(() => {
    if (!selectedOrgValId || !orgDataLists?.resources?.length) {
      return undefined;
    }

    return orgDataLists.resources.find(
      (item: IBuildOrganisation) => item.id === selectedOrgValId
    );
  }, [orgDataLists, selectedOrgValId]);

  const isCustomerBypass = useMemo(() => {
    const isOnPremValue = getIsOnPremValueFromOrgRecord(selectedOrgRecord);

    return /^(true|yes|1)$/i.test(String(isOnPremValue || "").trim());
  }, [selectedOrgRecord]);

  const getIsOnPremValueFromSites = (siteResponse: unknown) => {
    const responseObj = siteResponse as {
      resources?: Array<{
        metadata?: SiteMetaDataProps[];
      }>;
    };
    let isOnPremValue = "";

    responseObj?.resources?.forEach((siteResource) => {
      if (isOnPremValue) {
        return;
      }

      siteResource?.metadata?.forEach((item: SiteMetaDataProps) => {
        const metadataName = String(item?.name || "").trim().toLowerCase();

        if (!isOnPremValue && metadataName === "isonprem") {
          isOnPremValue = String(item?.value ?? "").trim();
        }
      });
    });

    return isOnPremValue;
  };

  const orgSiteSkipFn = () => {
    if (!selectedOrgValId) {
      return true;
    } else if (isCustomerBypass) {
      return false;
    }

    return !getOnBoardStatus().includes(orgStatusVals?.completedStatus);
  };

  const {
    data: siteDataLists,
    isLoading: siteLoading,
    isUninitialized: isSiteQueryUninitialized,
    refetch: siteRefresh
  } = useGetOrgSiteListsQuery(
    { orgId: selectedOrgValId },
    { skip: orgSiteSkipFn() }
  );

  const isSiteBypass = useMemo(() => {
    const isOnPremValue = getIsOnPremValueFromSites(siteDataLists);

    return /^(true|yes|1)$/i.test(String(isOnPremValue || "").trim());
  }, [siteDataLists]);

  const [triggerUpdate] = useUpdateGatewayMutation();
  const [syncOrgRequest, { isLoading: syncOrgLoading }] =
    useSyncOrganizationMutation();

  const { navigateToLandingPage } = useNavigateToLandingPage();

  // Determine if gateway is EBI-Platform type based on gatewayType pattern
  const isEBICognition = useMemo(() => {
    if (!deviceRec) {
      return false;
    }
    return deviceRec.gatewayType === "EBI-Platform";
  }, [deviceRec]);

  const gatewayEditBasicInfo: GatewayEditBasicInfoProps[] = useMemo(() => {
    if (isEBICognition) {
      const baseInfo = [
        { label: t(LOCALE_CONST.connectivityType), valueKey: "connectivity" } 
      ];
      if (deviceRec?.connectivity === "Connected") {
        baseInfo.push({ label: t(LOCALE_CONST.model), valueKey: "model" },{ label: t(LOCALE_CONST.gatewayType), valueKey: "gatewayType" });
      }
      return baseInfo;
    }
    return [{ label: t(LOCALE_CONST.gatewayType), valueKey: "gatewayType" }];
  }, [isEBICognition, t, deviceRec?.connectivity]);

  const triggerSyncOrg = async (type: string) => {
    try {
      let paramObj = {};

      if (type === "org") {
        paramObj = {
          orgId: selectedOrgValId,
          resourcesIds: [selectedOrgValId]
        };
      } else {
        paramObj = {
          orgId: selectedOrgValId,
          resourcesIds: Object.keys(siteMetaStatus)
        };
      }

      const resp = await syncOrgRequest(paramObj);
      setRetryType("");

      if (resp?.error) {

        const convertFn = (val: string) => t(val);
        const status =
          typeof resp.error === "object" &&
            resp.error !== null &&
            "status" in resp.error
            ? (resp.error as { status?: number }).status
            : undefined;
        const errContent = gatewayErrorStatusMessages(
          convertFn,
          t(LOCALE_CONST.failedSyncType, { orgType: type }),
          status,
          {
            401: t(LOCALE_CONST.unAuthFails),
            403: t(LOCALE_CONST.AuthForbidFails),
            404: t(LOCALE_CONST.projectIDNotFound),
            500: t(LOCALE_CONST.internalServerError)
          }
        );

        setToastInfo({
          show: true,
          type: "error",
          content: errContent
        });
      } else {
        if (type === "org") {
          orgRefresh();
        } else if (!isSiteQueryUninitialized) {
          siteRefresh();
        }
      }
    } catch (error) {
      setRetryType("");
    }
  };

  useEffect(() => {
    dispatch(setHeaderDetails({
      headerDetails: {
        type: 'edit',
        status: deviceRec?.status || '',
        title: t(LOCALE_CONST.editGateway) || ''
      }
    }));
  }, [deviceRec]);

  useEffect(() => {
    let orgStatusObj: { [key: string]: string } = {};
    let orgSiteStatusObj: { [key: string]: string } = {};

    ConfigurationVariables.ORG_STATUS_LISTS?.split(" ").forEach(
      (item: string, index: number) => {
        orgStatusObj[orgStatusLists[index]] = item;
      }
    );

    ConfigurationVariables.ORG_SITE_STATUS_NAME_LISTS?.split(" ").forEach(
      (item: string, index: number) => {
        orgSiteStatusObj[orgSiteStatusNameLists[index]] = item;
      }
    );

    setOrgStatusVals(orgStatusObj);
    setOrgSiteStatusVals(orgSiteStatusObj);
  }, []);

  useEffect(() => {
    const splitLocIDs = locationId?.split?.("/");

    setDeviceId(splitLocIDs?.[splitLocIDs?.length - 1] || "");
  }, [locationId]);

  useEffect(() => {
    if (deviceId) {
      refetchDeviceDetails();
    }
  }, [deviceId]);

  useEffect(() => {
  //added back the code to fetch non-cloud customer - please dont remove
    if (deviceRec) {
       dispatch(setRBMDetails({
        selectedOrg: selectedOrg || "",
        rbmAccessToken: rbmAccessToken || "",
        gatewayType: deviceRec.gatewayType || ""
      }));
      setDeviceName(deviceRec.name || "");
      // Populate hardware data from deviceRec
      setHardwareData({
        deviceType: deviceRec.gatewayType || "",
        hardwareModel: deviceRec.model || "",
        hardwareID:deviceRec.hardwareId || ""
      });

      if (deviceRec.organizationId) {
        if (
          orgLists?.find(
            (org: OrgListProps) => org.value === deviceRec.organizationId
          )
        ) {
          setSelectedOrgValId(deviceRec.organizationId || "");
        }
      }
    }
  }, [deviceRec, orgLists,selectedOrg,rbmAccessToken]);

  useEffect(() => {
    if (selectedOrgValId) {
      orgRefresh();
    }
  }, [selectedOrgValId]);

  useEffect(() => {
    setIsSiteLoading(false);
    if (
      selectedOrgValId &&
      selectedOrgSiteId?.length > 0 &&
      !isSiteQueryUninitialized
    ) {
      siteRefresh();
    }
  }, [selectedOrgSiteId, selectedOrgValId, isSiteQueryUninitialized]);

  const getFailedStatusMsg = (type: string) => {
    if (type === "Organization") {
      return t(LOCALE_CONST.orgFailedStatsMsg, { retryText: t(LOCALE_CONST.retryText), hwSupportTeam: t(LOCALE_CONST.hwSupportTeam) });
    } else {
      return t(LOCALE_CONST.siteFailedStatsMsg, { retryText: t(LOCALE_CONST.retryText), hwSupportTeam: t(LOCALE_CONST.hwSupportTeam) });
    }
  };

  useEffect(() => {
    let metaObj = { status: "", message: "" };

    if (isCustomerBypass) {
      setSiteMetaStatus({});
      setOrgMetaStatus(metaObj);
      return;
    }

    if (orgMetaData) {
      if (getOnBoardStatus().includes(orgStatusVals?.failedStatus)) {
        metaObj = {
          status: orgStatusVals?.failedStatus,
          message: getFailedStatusMsg("Organization")
        };
      } else if (getOnBoardStatus().includes(orgStatusVals?.inProgressStatus)) {
        metaObj = {
          status: orgStatusVals?.inProgressStatus,
          message: t(LOCALE_CONST.orgInProgressStatsMsg)
        };
      } else if (!getOnBoardStatus()) {
        metaObj = {
          status: orgStatusVals?.inProgressStatus,
          message: t(LOCALE_CONST.orgInProgressStatsMsg)
        };
      } else {
        //do nothing
      }

      if (metaObj.status) {
        setSiteLists([]);
        setSelectedOrgSiteId([]);
      }
    }

    setSiteMetaStatus({});
    setOrgMetaStatus(metaObj);
  }, [orgMetaData, isCustomerBypass]);

  useEffect(() => {
    const nonCompleteStatus: MetaDataStatusObjProps = {};
    const fallbackSelectedSites: GatewaySiteProps[] = (deviceRec?.site || []).map(
      (site: GatewaySiteProps) => ({
        id: site?.id || "",
        name: site?.name || ""
      })
    );
    const fallbackSiteLists: ISiteListProps[] = fallbackSelectedSites.map(
      (site: GatewaySiteProps) => ({
        id: site.id,
        name: site.name,
        type: "Site",
        metadata: []
      })
    );
    const shouldUseDeviceSiteFallback =
      !siteDataLists?.resources?.length &&
      (!selectedOrgValId || selectedOrgValId === (deviceRec?.organizationId || ""));

    if (siteDataLists?.resources?.length > 0) {
      const accessSites: GatewaySiteProps[] = [];
      const siteValLists: ISiteListProps[] = [];

      siteDataLists?.resources?.forEach((val: ISiteListProps) => {
        if (val?.type === "Site") {
          const siteRec = deviceRec?.site?.find(
            (site: GatewaySiteProps) => site.id === val?.id
          );

          val?.metadata?.forEach((item: SiteMetaDataProps) => {
            if (
              item.name === orgSiteStatusVals?.EomSiteId
            ) {
              if (!item.value) {
                nonCompleteStatus[val?.id || ""] = {
                  status: orgStatusVals?.failedStatus,
                  message: getFailedStatusMsg("Site")
                };
              }
            }
          });

          siteValLists.push({
            name: val?.name || "",
            id: val?.id || "",
            metadata: val?.metadata || [],
            isChecked: siteRec ? true : false,
            type: val?.type || "",
            modelId: val?.modelId || "",
            externalId: val?.externalId || "",
          });

          if (siteRec) {
            accessSites.push(siteRec);
          }
        }
      });

      setSiteLists(
        [...siteValLists].sort((a, b) => a.name.localeCompare(b.name))
      );

      if (accessSites?.length > 0) {
        setSelectedOrgSiteId(accessSites || []);
      } else if (shouldUseDeviceSiteFallback) {
        setSelectedOrgSiteId(fallbackSelectedSites);
      }
    } else if (shouldUseDeviceSiteFallback) {
      setSiteLists(
        [...fallbackSiteLists].sort((a, b) => a.name.localeCompare(b.name))
      );
      setSelectedOrgSiteId(fallbackSelectedSites);
    } else {
      setSiteLists([]);
      setSelectedOrgSiteId([]);
    }

    //setSiteMetaStatus(nonCompleteStatus);
  }, [siteDataLists, deviceRec, selectedOrgValId]);

  useEffect(() => {
    if (retryType) {
      setToastInfo({ show: false, type: "success", content: "" });
      triggerSyncOrg(retryType);
    }
  }, [retryType]);

  useEffect(() => {
    if (toastInfo.show && toastInfo.type === "success") {
      setTimeout(() => {
        // API call to update device name
        dispatch(setDeviceListUpdated({ deviceListUpdated: true }));

        navigateToLandingPage();
      }, 1200);
    }
  }, [toastInfo]);

  const setError = (field: string, value: boolean) => {
    setErrorState((prevState) => ({
      ...prevState,
      [field]: value,
    }));
  };

  const handleInputChange = useCallback((e: React.ChangeEvent<HTMLInputElement>) => {
    const newValue = e.target.value || "";
    // clear errors on change
    setError("showNameError", false);
    setError("showNameTypeError", false);
    setDeviceName(newValue);

    // detect invalid patterns using shared validator
    if (!validateDeviceName(newValue)) {
      setError("showNameTypeError", true);
    }
  }, [deviceName]);

  // Validation helpers are provided by utils/utility for consistency across components

  // Helper to update hardware error state
  const setHardwareError = (field: string, value: boolean) => {
    setHardwareErrorState((prevState) => ({
      ...prevState,
      [field]: value,
    }));
  };

  const handleHardwareInputChange = useCallback(
    (e: React.ChangeEvent<HTMLInputElement | HTMLSelectElement>, type: string) => {
      const value = e.target.value;
      // Update the hardware data first
      setHardwareData((prev: any) => ({
        ...prev,
        [type]: value,
      }));

      // Perform validation and update error state
      if (type === "deviceType") {
        setHardwareError("showDeviceTypeError", false);
        // reset hardwareModel when deviceType changes
        setHardwareData((prev: any) => ({
          ...prev,
          deviceType: value,
          hardwareModel: prev.hardwareModel || "",
        }));
      } else if (type === "hardwareModel") {
        setHardwareError("showHardwareModelError", false);
        setHardwareError("showHardwareModelTypeError", false);
        if (value !== "" && !validateHardwareModel(value)) {
          setHardwareError("showHardwareModelTypeError", true);
        }
      } else if (type === "hardwareID") {
        setHardwareError("showHardwareIDError", false);
        setHardwareError("showValidHardwareIDError", false);
        if (value !== "" && !validateAlphanumeric(value)) {
          setHardwareError("showValidHardwareIDError", true);
        }
      }

      setIsFormChanged(true);
    },
    []
  );

  const handleOrgValueListChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    setSelectedOrgValId(e.target.value || "");
    setIsSiteLoading(true);
    setSelectedOrgSiteId([]);
  };

  const setSiteValueSelection = (lists: GatewaySiteProps[]) => setSelectedOrgSiteId(lists);

  useEffect(() => {
    const selectedOrgValIdData = selectedOrgValId !== "" ? selectedOrgValId : null;
    const selectedOrgSiteIdData = selectedOrgSiteId?.length > 0 ? selectedOrgSiteId : null;
    
    // Check if hardware data changed (for EBI-Platform with Disconnected connectivity)
    const hardwareDataChanged = isEBICognition && deviceRec?.connectivity === "Disconnected" && (
      hardwareData.hardwareModel !== (deviceRec?.model || "") ||
      hardwareData.hardwareID !== (deviceRec?.hardwareId || "")
    );
    
    if (selectedOrgValIdData !== deviceRec?.organizationId || 
        deviceName !== deviceRec?.name || 
        !areListsEqual(deviceRec?.site, selectedOrgSiteIdData) ||
        hardwareDataChanged) {
      setIsFormChanged(true);
    } else {
      setIsFormChanged(false);
    }
  }, [handleOrgValueListChange, handleInputChange, deviceRec, hardwareData, isEBICognition]);

  const onCancelAction = () => navigateToLandingPage();

  const onUpdateAction = async () => {
    setIsConfirmationModalOpen(false);
    try {
      if (
        deviceName.length < BASIC_INFO_MIN_CHARS ||
        deviceName.length > BASIC_INFO_MAX_CHARS ||
        !deviceName.trim()
      ) {
        setError("showNameError", true);
        setToastInfo({
          show: true,
          type: "error",
          content: t(LOCALE_CONST.gwNameValidation)
        });
        setTimeout(() => {
          setToastInfo({
            show: false,
            type: "error",
            content: t(LOCALE_CONST.gwNameValidation)
          });
        }, 3000);
        return;
      }

      // Validate hardware details for EBI-Platform devices with Disconnected connectivity
      if (isEBICognition && deviceRec?.connectivity === "Disconnected") {
        const hardwareModelValue = hardwareData.hardwareModel?.trim() || "";
        const hardwareIDValue = hardwareData.hardwareID?.trim() || "";

        if (!hardwareModelValue) {
          setToastInfo({
            show: true,
            type: "error",
            content:  t(LOCALE_CONST.hardwareModelRequired)
          });
          setTimeout(() => {
            setToastInfo({ show: false, type: "error", content: "" });
          }, 3000);
          return;
        }
        
        if (!hardwareIDValue) {
          setToastInfo({
            show: true,
            type: "error",
            content: t(LOCALE_CONST.hardwareIDValidation)
          });
          setTimeout(() => {
            setToastInfo({ show: false, type: "error", content: "" });
          }, 3000);
          return;
        }
      }

      const inputParams: { [key: string]: null | string | GatewaySiteProps[] } =
        {};

      if (selectedOrgValId && selectedOrgSiteId?.length === 0) {
        //do nothing
      } else {
        inputParams["name"] = deviceName;
        inputParams["organizationId"] = selectedOrgValId || null;
        inputParams["organizationName"] =
          orgLists?.find((org: OrgListProps) => org.value === selectedOrgValId)
            ?.text || null;
        inputParams["site"] = selectedOrgSiteId.length > 0 ? selectedOrgSiteId : null;

        // Add hardware details for EBI-Platform devices
        if (isEBICognition && deviceRec?.connectivity === "Disconnected") {
          inputParams["hardwareId"] = hardwareData.hardwareID || null;
          inputParams["model"] = hardwareData.hardwareModel || null;
          inputParams["gatewayType"] = hardwareData.deviceType || null;

        }

        setApiLoading(true);
        setToastInfo({ show: false, type: "success", content: "" });

        const resp = await triggerUpdate({ deviceId, gateway: inputParams });

        setApiLoading(false);
        const convertFn = (val: string) => t(val);

        if (resp?.error) {
          setToastInfo({
            show: true,
            type: "error",
            content:
              gatewayErrorStatusMessages(
                convertFn,
                t(LOCALE_CONST.failedSaveGateway),
                (resp?.error && "status" in resp.error) ? (resp.error as any).status : undefined
              )
          });
        } else {
          setToastInfo({
            show: true,
            type: "success",
            content: t(LOCALE_CONST.gwDetailsUpdatedSuccess)
          });
        }
      }
    } catch (error) {
      setToastInfo({
        show: true,
        type: "error",
        content:
          (error as { data?: { message?: string } })?.data?.message ||
          t(LOCALE_CONST.failedSaveGateway)
      });
    }
  };

  const getEmailHightlightText = (text: string) => {
    const reg = new RegExp(`(${emailText})`, "gi");
    const textParts = text.split(reg);

    const renderEmailText = (part: string) => {
      if (part.match(reg)) {
        return (
          <a
            className="retry-text-link-cls"
            data-testid="retry-wrap-email-id"
            href={`mailto:${ConfigurationVariables.HONEYWELL_SUPPORT_EMAILID}`}
          >
            {part}
          </a>
        );
      } else {
        return part;
      }
    };

    return (
      <>
        {textParts?.map((part: string, index: number) => (
          <span key={index}>{renderEmailText(part)}</span>
        ))}
      </>
    );
  };

  const highlightQueryText = (
    type: string,
    text: string,
    filterValue: string
  ) => {
    const reg = new RegExp(`(${filterValue})`, "gi");
    const textParts = text.split(reg);

    const renderLinkText = (part: string) => {
      if (part.match(reg)) {
        return (
          <span
            className="retry-text-link-cls"
            data-testid="retry-wrap-id"
            onClick={() => setRetryType(type)}
          >
            {t(LOCALE_CONST.retryText)}
          </span>
        );
      } else {
        return getEmailHightlightText(part);
      }
    };

    return (
      <span className="error-wrap-text-cls">
        {textParts?.map((part: string, index: number) => (
          <span key={index}>{renderLinkText(part)}</span>
        ))}
      </span>
    );
  };

  const isOrgErrorFn = () => {
    if (selectedOrgValId && orgMetaStatus.status) {
      if (orgMetaStatus.status === orgStatusVals?.failedStatus) {
        return (
          <ControlError className="w-[51rem]" showIcon>
            {highlightQueryText("org", orgMetaStatus.message || "", t(LOCALE_CONST.retryText))}
          </ControlError>
        );
      } else {
        return (
          <ControlError className="w-[51rem]" showIcon>
            {orgMetaStatus.message || ""}
          </ControlError>
        );
      }
    } else {
      return null;
    }
  };

  const isSiteErrorFn = () => {
    if (selectedOrgValId && !orgMetaStatus.status) {
      if (
        selectedOrgSiteId?.length > 0 &&
        Object.keys(siteMetaStatus).length > 0
      ) {
        if (
          selectedOrgSiteId.some(
            (site: GatewaySiteProps) => siteMetaStatus[site.id]
          )
        ) {
          return (
            <ControlError className="w-[51rem]" showIcon>
              {highlightQueryText(
                "site",
                orgMetaStatus.message || "",
                t(LOCALE_CONST.retryText)
              )}
            </ControlError>
          );
        } else {
          return null;
        }
      } else if (selectedOrgSiteId?.length === 0) {
        return <ControlError showIcon>{t(LOCALE_CONST.siteValidation)}</ControlError>;
      } else {
        return null;
      }
    } else {
      return null;
    }
  };

  // Function to determine if any loading state is active
  const isAnyLoading = () =>
    isLoading ||
    apiLoading ||
    syncOrgLoading;

  const normalizeSpaces = (str: string) => str.toLowerCase().replace(/\s+/g, ' ').trim();
  const orgListsSearch = orgLists?.filter((org: any) =>
    normalizeSpaces(org.text).includes(normalizeSpaces(orgSearchQuery))
  );

  const getStatusIcon = (status: string) => {
    const color = getStatusColor(status);
    switch (status) {
      case GatewayStatus.Active:
        return <SuccessIcon strokeWidth={2} color={color} />;
      case GatewayStatus.Deactivated:
        return <InfoIcon strokeWidth={2} color={color} />;
      case GatewayStatus.Pending:
        return <ProgressIcon strokeWidth={2} color={color} />;
      case GatewayStatus.Error:
        return <AlertIcon strokeWidth={2} color={color} />;
      case GatewayStatus.CertificateExpired:
        return <AlertIcon strokeWidth={2} color={color} />;
      default:
        return undefined;
    }
  };

  const isSaveDisabled = () => {
    if (isFormChanged) {
      if (!selectedOrgRecord) {
        return true;
      }if (selectedOrgValId && selectedOrgSiteId?.length === 0) {
        return true;
      } else {
        return false;
      }
    } else {
      return true
    }
  };

  const onSaveAction = () => {
    // Check if org or site has been modified from original values
    const orgChanged = selectedOrgValId !== (deviceRec?.organizationId || "");
    const siteChanged = !areListsEqual(deviceRec?.site, selectedOrgSiteId?.length > 0 ? selectedOrgSiteId : null);
    
    // Show confirmation modal only when org or site has been modified
    if (orgChanged || siteChanged) {
      setIsConfirmationModalOpen(true);
    } else {
      onUpdateAction();
    }
  };
  
  const convertFn = (value: string) => t(value);
  if (isError) {
    // Handle error state
    return (
      <div className="edit-device-error-container">
        <EmptyState variant="4xx"
          title={"status" in deviceDataError && typeof deviceDataError.status === "number"
            ? gatewayErrorStatusMessages(convertFn, `${t(LOCALE_CONST.somethingWentWrong)}...`, deviceDataError.status, { 404: t(LOCALE_CONST.clientError_404) })
            : `${t(LOCALE_CONST.somethingWentWrong)}...`}
          description={`${t(LOCALE_CONST.tryReloading)}. (${t(LOCALE_CONST.errCode, { errorCode: "status" in deviceDataError ? deviceDataError.status : t(LOCALE_CONST.error) })})`}
        >
          <Button className="mx-auto block" onClick={refetchDeviceDetails}>{t(LOCALE_CONST.reloadPage)}</Button>
        </EmptyState>
      </div>
    )
  }
  else {
    return (
      <div>
        {toastInfo.show && (
          <div className="toast-container">
            <div
              onClick={() =>
                setToastInfo({ show: false, type: "success", content: "" })
              }
              style={{ cursor: "pointer" }}
            >
              <Toast
                content={""}
                subtitle={""}
                lifetime={3000}
                showClose={true}
                showDismiss={false}
                title={toastInfo.content || ""}
                type={toastInfo.type || "success"}
                id={`edit-device-toast-${deviceId}`}
              />
            </div>
          </div>
        )}

        <PageLoader isLoading={isAnyLoading()} />
        <div>
          <Card className="mx-auto mt-6 w-[25rem] min-w-full max-w-2xl edit-dev-card-block-cls">
            <span className="iot-basic-info-text p-5 block">{t(LOCALE_CONST.basicInfo)}</span>
            <CardContent top="none" className="p-5">
              <Control className="w-[25rem] mt-2" spacingAbove spacingBelow={false}>
                <Label
                  className="font-normal iot-label-name-txt-cls"
                  required
                  requiredText=""
                >
                  {t(LOCALE_CONST.name)}
                </Label>
                <Input
                  id={`name-${deviceRec?.id || "-"}`}
                  value={deviceName}
                  onChange={handleInputChange}
                  data-testid="device-name"
                  disabled={
                    deviceRec?.site !== null &&
                    deviceRec?.organizationName !== null
                  }
                  maxLength={50}
                  error={errorState.showNameError || errorState.showNameTypeError}
                />
                {errorState.showNameTypeError && (
                  <ControlError showIcon>{t(LOCALE_CONST.invalidCharacter)}</ControlError>
                )}
                {errorState.showNameError && (
                  <ControlError showIcon>{t(LOCALE_CONST.requiredField)}</ControlError>
                )}
              </Control>
              <ControlHelper className="helper-text-edit-name" style={{ padding: "0.5rem 0" }}>
                {t(LOCALE_CONST.gatewayNameValidText)}
              </ControlHelper>
              {gatewayEditBasicInfo?.map(
                (info: GatewayEditBasicInfoProps, index: number) => {
                  const getDeviceVal = () => {
                    if (deviceRec) {
                      const value = deviceRec[
                        info.valueKey as Exclude<
                          keyof DeviceListProps,
                          "registrationKey"
                        >
                      ] || "-";
                      
                      // For EBI-Platform, display formatted status
                      if (info.valueKey === "status" && isEBICognition) {
                        return displayStatus(value as string);
                      }
                      
                      return value;
                    } else {
                      return "-";
                    }
                  };

                  return (
                    <div className="iot-device-type-block-cls" key={index}>
                      <div className="iot-device-type-txt-cls">{info.label}</div>
                      <div className="iot-device-type-val-cls">
                        {getDeviceVal()}
                      </div>
                    </div>
                  );
                }
              )}
            </CardContent>
          </Card>
        </div>
        {deviceRec?.connectivity === "Disconnected" && isEBICognition && (
          <div>
            <HardwareDetails
            hardwareData={hardwareData}
            errorState={hardwareErrorState}
            deviceTypeOptions={hardwareDetailsData as string[] || []}
            onInputChange={handleHardwareInputChange}
            isEditMode={true}
          />
        </div>)}

        <div>
          <Card className="mx-auto mt-6 w-[25rem] min-w-full max-w-2xl edit-dev-card-block-cls">
            <span className="iot-basic-info-text p-5 block">{t(LOCALE_CONST.registrationDetails)}</span>
            {/* <Banner className="items-center" canClose={false} variant="page" type="warning" title="" description={t(LOCALE_CONST.orgAndSiteOneTimeActionText)} /> */}
            <CardContent top="none" className="p-5">
              <Control className="w-[25rem] mt-2" spacingAbove spacingBelow>
                <Label className="font-normal iot-label-name-txt-cls">
                  {t(LOCALE_CONST.organization)}
                </Label>
                <Select
                  height={10}
                  // disabled={deviceRec?.modelPublished !== false}
                  id="org-combo-id"
                  onBlur={function noRefCheck() { }}
                  onChange={handleOrgValueListChange}
                  onFocus={function noRefCheck() { }}
                  placeholder={(deviceRec?.site !== null &&
                    deviceRec?.organizationName !== null) ? deviceRec?.organizationName : t(LOCALE_CONST.selectOrg)}
                  size="large"
                  value={ selectedOrgValId || ""}
                  data-testid="select-org"
                  search={
                    <SelectOptionSearch
                      label={t(LOCALE_CONST.selectOrg)}
                      onChange={(e) => {
                        if (validateInput(e.target.value)) {
                          setOrgSearchQuery(e.target.value);
                        }
                      }}
                      placeholder={t(LOCALE_CONST.selectOrg)}
                      value={orgSearchQuery}
                    />
                  }
                  disabled={deviceRec?.site !== null &&
                    deviceRec?.organizationName !== null}
                >
                  {orgListsSearch?.map((org: OrgListProps, index: number) => (
                    <SelectOption
                      key={index}
                      label={org.text || "-"}
                      value={org.value}
                    />
                  ))}
                </Select>
                {isOrgErrorFn()}
              </Control>
              {/* {!isSiteLoading && (
              <Control className="w-[25rem] mt-2" spacingAbove spacingBelow>
                <Label className="font-normal iot-label-name-txt-cls">
                  Site
                </Label>
                <Select
                  height={10}
                  // disabled={deviceRec?.modelPublished !== false}
                  id="site-combo-id"
                  onBlur={function noRefCheck() {}}
                  onChange={handleSiteValueListChange}
                  onFocus={function noRefCheck() {}}
                  placeholder="Select Site"
                  size="large"
                  multiple={true}
                  value={
                    selectedOrgSiteId?.map(
                      (site: GatewaySiteProps) => site.id
                    ) || []
                    
                  }
                  disabled={Boolean(orgMetaStatus.status || !selectedOrgValId)}
                  data-testid="select-site"
                  search={
                    <SelectOptionSearch
                      label="Search Site"
                      onChange={(e) => {
                        if (validateInput(e.target.value)) {
                          setSiteSearchQuery(e.target.value);
                        }
                      }}
                      placeholder="Search Site"
                      value={siteSearchQuery}
                    />
                  }
                >
                  {siteListsSearch?.map((site: OrgListProps, index: number) => (
                    <SelectOption
                      key={index}
                      label={site.text || "-"}
                      value={site.value || "-"}
                    />
                  ))}
                </Select>
                {isSiteErrorFn()}
              </Control>
            )} */}

              {(isSiteLoading || orgMetaLoading || orgMetaFetching) ?
                <ContentLoader className="my-[50px]">  {t(LOCALE_CONST.loadingDetails)}... </ContentLoader> :
                <OverviewTable
                  isEdit={true}
                  data={siteLists || []}
                  selectedData={selectedOrgSiteId}
                  title={t(LOCALE_CONST.associatedSites)}
                  setSiteValueSelection={setSiteValueSelection}
                    // isDisabled={Boolean((!isCustomerBypass && orgMetaStatus.status) || !selectedOrgValId)}
                  isNonCloudCustomer={isSiteBypass}
                  sitesAlreadyAssoData={deviceRec?.site}
                />
              }
            </CardContent>
          </Card>
        </div>

        <div className="edit-device-buttons-block-cls">
          <div className="edit-device-button-container">
            <Button
              variant="secondary"
              className="cancel-btn-cls"
              onClick={onCancelAction}
            >
              {t(LOCALE_CONST.cancel)}
            </Button>
            <Button
              variant="primary"
              onClick={onSaveAction}
              disabled={isSaveDisabled()}
              className="save-btn-cls"
            >
              {t(LOCALE_CONST.save)}
            </Button>
          </div>
        </div>
        {/* Confirmation Modal */}
        {isConfirmationModalOpen && (
          <Modal
            backdrop
            onModalClose={() => setIsConfirmationModalOpen(false)}
            title="Alert"
            footerRight={
              <div>
                <Button
                  variant="secondary"
                  onClick={() => setIsConfirmationModalOpen(false)}
                >
                  No
                </Button>
                <Button
                  variant="primary"
                  className="bulk-assignment-confirm-button ml-[12px]"
                  onClick={onUpdateAction}
                >
                  Yes
                </Button>
              </div>
            }
            className="w-[540px] edit-device-confirmation-modal"
          >
            <p>
              {t(LOCALE_CONST.editSiteModalDescOne)} <br /><br />
              {t(LOCALE_CONST.editSiteModalDescTwo)} <br /><br />
              {t(LOCALE_CONST.eidtConfirmationText)}
            </p>
          </Modal>
        )}
        {/* Modal End */}
      </div>
    );
  }
};

export default EditDevice;
